import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { Car, TrendingUp, TrendingDown, Wallet, CalendarClock, AlarmClock, Split, Repeat, Check, X, Search } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useSort, SortHead } from "../components/SortHead";
import { useCompany } from "../context/CompanyContext";
import api, { fmtNum, fmtDate } from "../lib/api";

const THIS_YEAR = new Date().getFullYear();
const ALL_TIME = "__all__";

export default function VehiclesPage() {
  const { company, loaded } = useCompany();
  const [scope, setScope] = useState(String(THIS_YEAR));
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailTxs, setDetailTxs] = useState(null);
  const [idle, setIdle] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [banks, setBanks] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignVeh, setAssignVeh] = useState("");
  const [assignSearch, setAssignSearch] = useState("");
  const [plans, setPlans] = useState([]);
  const [assignSel, setAssignSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [leaseOpen, setLeaseOpen] = useState(false);
  const [lease, setLease] = useState(null);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/banks").then((r) => setBanks(r.data));
  }, []);

  const loadProfit = () => {
    const params = scope === ALL_TIME ? {} : { year: parseInt(scope, 10) };
    api.get("/vehicles/profit", { params }).then((r) => setData(r.data));
  };

  const openAssign = async () => {
    const r = await api.get("/transactions", { params: { status: "planirano", limit: 500 } });
    setPlans(r.data.filter((t) => !t.vehicle));
    setAssignSel([]);
    setAssignSearch("");
    setAssignVeh("");
    setAssignOpen(true);
  };
  const confirmAssign = async () => {
    setBusy(true);
    try {
      const v = vehicles.find((x) => x.plate === assignVeh);
      const r = await api.post("/vehicles/assign-plans", { plate: assignVeh, vehicle_id: v?.id || "", tx_ids: assignSel });
      toast.success(`Vozilo ${assignVeh} povezano s ${r.data.updated} planiranih stavki`);
      setAssignOpen(false);
      loadProfit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri povezivanju");
    } finally {
      setBusy(false);
    }
  };

  const openLease = () => {
    setLease({ vehicle: "", counterparty: "", amount: "", currency: "BAM", bank_id: banks[0]?.id || "", first_due: "", end_month: "" });
    setLeaseOpen(true);
  };
  const saveLease = async () => {
    const amt = parseFloat(String(lease.amount).replace(",", "."));
    if (!lease.vehicle || !amt || amt <= 0 || !lease.first_due) {
      toast.error("Odaberite vozilo te unesite iznos i prvo dospijeće");
      return;
    }
    setBusy(true);
    try {
      const v = vehicles.find((x) => x.plate === lease.vehicle);
      await api.post("/recurring", {
        name: `Najam ${lease.vehicle}${lease.counterparty ? ` — ${lease.counterparty}` : ""}`,
        bank_id: lease.bank_id,
        type: "naplata",
        currency: lease.currency,
        amount: amt,
        category: "najam vozila",
        day_of_month: parseInt(lease.first_due.slice(8, 10), 10),
        start_month: lease.first_due.slice(0, 7),
        end_month: lease.end_month || null,
        vehicle: lease.vehicle,
        vehicle_id: v?.id || "",
      });
      toast.success("Dugoročni najam dodan kao fiksni mjesečni prihod — rate prebacite na stranici Fiksni troškovi/prihodi");
      setLeaseOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Greška pri spremanju najma");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get("/vehicles/idle").then((r) => setIdle(r.data.items)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = scope === ALL_TIME ? {} : { year: parseInt(scope, 10) };
    api.get("/vehicles/profit", { params }).then((r) => setData(r.data));
  }, [scope]);

  const openDetail = (v) => {
    setDetail(v);
    setDetailTxs(null);
    api.get("/transactions", { params: { vehicle: v.plate, limit: 1000 } }).then((r) => {
      const y = scope === ALL_TIME ? null : scope;
      setDetailTxs(y ? r.data.filter((t) => t.date.startsWith(y)) : r.data);
    });
  };

  const items = data?.items || [];
  const accessors = {
    plate: (v) => v.plate,
    income: (v) => v.income_eur,
    expense: (v) => v.expense_eur,
    net: (v) => v.net_eur,
    count: (v) => v.count,
    last: (v) => v.last_date || "",
  };
  const { sorted, sort, toggle } = useSort(items, accessors);
  const idleByPlate = Object.fromEntries(idle.map((i) => [i.plate, i]));

  if (loaded && company && company.features?.vehicles === false) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6 fade-up" data-testid="vehicles-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Rent a Car flota</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Vozila</h1>
          <p className="text-sm text-[#475569] mt-2" data-testid="vehicles-count">
            {items.length} {items.length === 1 ? "vozilo" : "vozila"} · zarada = ostvareni prihodi od najma − ostvareni troškovi (servis, štete, registracija, leasing...)
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="assign-plans-btn" variant="outline" onClick={openAssign} className="rounded-sm border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488]/5">
            <Split className="w-4 h-4 mr-2" /> Poveži planirane stavke
          </Button>
          <Button data-testid="add-lease-btn" onClick={openLease} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
            <Repeat className="w-4 h-4 mr-2" /> Dugoročni najam
          </Button>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="vehicles-scope-select" className="w-40 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={String(THIS_YEAR)}>{THIS_YEAR}. godina</SelectItem>
              <SelectItem value={String(THIS_YEAR - 1)}>{THIS_YEAR - 1}. godina</SelectItem>
              <SelectItem value={ALL_TIME}>Sve vrijeme</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="fleet-income-card">
            <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-[#0D9488]" /><p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Prihodi flote</p></div>
            <p className="font-heading text-2xl font-bold text-[#0D9488] tabular-nums">+{fmtNum(data.totals.income_eur, "€")}</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="fleet-expense-card">
            <div className="flex items-center gap-2 mb-2"><TrendingDown className="w-4 h-4 text-[#DC2626]" /><p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B]">Troškovi flote</p></div>
            <p className="font-heading text-2xl font-bold text-[#DC2626] tabular-nums">−{fmtNum(data.totals.expense_eur, "€")}</p>
          </div>
          <div className={`rounded-sm p-5 text-white ${data.totals.net_eur < 0 ? "bg-[#DC2626]" : "bg-[#0D9488]"}`} data-testid="fleet-net-card">
            <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 opacity-70" /><p className="text-xs uppercase tracking-[0.15em] opacity-70">Neto zarada flote</p></div>
            <p className="font-heading text-2xl font-bold tabular-nums">{fmtNum(data.totals.net_eur, "€")}</p>
          </div>
        </div>
      )}

      {idle.length > 0 && (
        <div data-testid="vehicles-idle-banner" className="bg-white border border-[#D97706]/50 rounded-sm overflow-hidden">
          <div className="px-4 py-3 bg-[#D97706]/10 border-b border-[#D97706]/30 flex items-center gap-2">
            <AlarmClock className="w-4 h-4 text-[#D97706]" />
            <p className="text-sm font-semibold text-[#0F172A]">Slaba iskorištenost ({idle.length}) — bez najma 30+ dana, a troškovi teku</p>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {idle.map((v, i) => (
              <div key={v.plate} data-testid={`vehicles-idle-item-${i}`} className="px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="font-medium text-[#0F172A] flex items-center gap-2 min-w-[160px]">
                  <Car className="w-4 h-4 text-[#D97706]" /> {v.plate}{v.label ? ` · ${v.label}` : ""}
                </span>
                <span className="text-[#475569]">
                  {v.idle_days != null ? `bez najma ${v.idle_days} d (zadnji ${fmtDate(v.last_income_date)})` : "još nijedan najam"}
                </span>
                {v.costs_since_eur > 0 && <span className="tabular-nums text-[#DC2626]">troškovi od tada −{fmtNum(v.costs_since_eur, "€")}</span>}
                {v.monthly_recurring_eur > 0 && <span className="tabular-nums text-[#DC2626]">fiksno −{fmtNum(v.monthly_recurring_eur, "€")}/mj</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-[#CBD5E1] rounded-sm p-10 text-center text-sm text-[#64748B]" data-testid="vehicles-empty-state">
          <Car className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          Još nema vozila. Vozila se sinkroniziraju iz VTZ Rent a Car aplikacije, a transakcije se označavaju vozilom pri unosu.
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                <SortHead label="Vozilo" sortKey="plate" sort={sort} onToggle={toggle} />
                <SortHead label="Prihodi" sortKey="income" sort={sort} onToggle={toggle} className="text-right" align="right" />
                <SortHead label="Troškovi" sortKey="expense" sort={sort} onToggle={toggle} className="text-right" align="right" />
                <SortHead label="Neto zarada" sortKey="net" sort={sort} onToggle={toggle} className="text-right" align="right" />
                <SortHead label="Transakcija" sortKey="count" sort={sort} onToggle={toggle} className="text-right" align="right" />
                <SortHead label="Zadnja aktivnost" sortKey="last" sort={sort} onToggle={toggle} />
                <TableHead>U najavi (planirano)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="vehicles-table-body">
              {sorted.map((v) => (
                <TableRow
                  key={v.plate}
                  data-testid={`vehicle-row-${v.plate}`}
                  onClick={() => openDetail(v)}
                  className={`cursor-pointer transition-colors duration-200 ${v.net_eur < 0 ? "bg-[#DC2626]/5 hover:bg-[#DC2626]/10" : "hover:bg-[#F8FAFC]"}`}
                >
                  <TableCell className="text-sm font-semibold">
                    <span className="flex items-center gap-2">
                      <Car className={`w-4 h-4 ${v.net_eur < 0 ? "text-[#DC2626]" : "text-[#0D9488]"}`} />
                      {v.plate}
                    </span>
                    {v.label && <span className="block text-[11px] font-normal text-[#64748B] ml-6">{v.label}</span>}
                    {idleByPlate[v.plate] && (
                      <span data-testid={`vehicle-idle-badge-${v.plate}`} className="inline-flex items-center gap-1 mt-1 ml-6 text-[11px] font-semibold px-1.5 py-0.5 rounded-sm bg-[#D97706]/15 text-[#92400E]">
                        <AlarmClock className="w-3 h-3" />
                        {idleByPlate[v.plate].idle_days != null ? `bez najma ${idleByPlate[v.plate].idle_days} d` : "još nijedan najam"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm font-semibold text-[#0D9488]">+{fmtNum(v.income_eur, "€")}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm font-semibold text-[#DC2626]">−{fmtNum(v.expense_eur, "€")}</TableCell>
                  <TableCell className={`text-right tabular-nums text-sm font-bold ${v.net_eur < 0 ? "text-[#DC2626]" : "text-[#0F172A]"}`} data-testid={`vehicle-net-${v.plate}`}>
                    {fmtNum(v.net_eur, "€")}
                    {v.net_eur < 0 && <Badge className="ml-2 rounded-sm text-[10px] bg-[#DC2626]/15 text-[#DC2626] hover:bg-[#DC2626]/15">u minusu</Badge>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-[#475569]">{v.count}</TableCell>
                  <TableCell className="text-sm tabular-nums text-[#475569]">{v.last_date ? fmtDate(v.last_date) : "—"}</TableCell>
                  <TableCell className="text-xs text-[#64748B] tabular-nums">
                    {v.planned_in_eur > 0 && <span className="text-[#D97706]">+{fmtNum(v.planned_in_eur, "€")}</span>}
                    {v.planned_in_eur > 0 && v.planned_out_eur > 0 && " / "}
                    {v.planned_out_eur > 0 && <span className="text-[#D97706]">−{fmtNum(v.planned_out_eur, "€")}</span>}
                    {v.planned_in_eur === 0 && v.planned_out_eur === 0 && "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vehicle-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Car className="w-5 h-5 text-[#0D9488]" /> {detail?.plate}{detail?.label ? ` — ${detail.label}` : ""}
            </DialogTitle>
            <DialogDescription>
              {scope === ALL_TIME ? "Sve transakcije vozila" : `Transakcije vozila u ${scope}. godini`}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-[#F1F5F9] rounded-sm p-3">
                <p className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">Prihodi</p>
                <p className="font-heading font-bold text-[#0D9488] tabular-nums">+{fmtNum(detail.income_eur, "€")}</p>
              </div>
              <div className="bg-[#F1F5F9] rounded-sm p-3">
                <p className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">Troškovi</p>
                <p className="font-heading font-bold text-[#DC2626] tabular-nums">−{fmtNum(detail.expense_eur, "€")}</p>
              </div>
              <div className="bg-[#F1F5F9] rounded-sm p-3">
                <p className="text-[11px] uppercase tracking-wider text-[#64748B] font-semibold">Neto</p>
                <p className={`font-heading font-bold tabular-nums ${detail.net_eur < 0 ? "text-[#DC2626]" : "text-[#0F172A]"}`}>{fmtNum(detail.net_eur, "€")}</p>
              </div>
            </div>
          )}
          {!detailTxs ? (
            <p className="text-sm text-[#64748B]">Učitavanje...</p>
          ) : detailTxs.length === 0 ? (
            <p className="text-sm text-[#64748B]">Nema transakcija u odabranom razdoblju.</p>
          ) : (
            <div className="space-y-2" data-testid="vehicle-detail-txs">
              {detailTxs.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 border-b border-[#F1F5F9] pb-2 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate">{t.counterparty || t.category}</p>
                    <p className="text-xs text-[#64748B]">{fmtDate(t.date)} · {t.category}</p>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-2">
                    {t.status === "planirano" && (
                      <Badge className="rounded-sm text-[10px] bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/15 gap-1"><CalendarClock className="w-3 h-3" /> plan</Badge>
                    )}
                    <p className={`text-sm font-semibold tabular-nums ${t.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                      {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount, t.currency === "EUR" ? "€" : "KM")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog" className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Split className="w-4 h-4 text-[#0D9488]" /> Poveži planirane stavke s vozilom</DialogTitle>
            <DialogDescription>Postojeći planirani troškovi/naplate bez vozila — odaberite vozilo pa označite stavke koje mu pripadaju. Ulaze u zaradu vozila.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Vozilo</Label>
              <Select value={assignVeh} onValueChange={setAssignVeh}>
                <SelectTrigger data-testid="assign-vehicle-select"><SelectValue placeholder="Odaberi vozilo" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.plate}>{v.plate}{v.label ? ` — ${v.label}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Traži stavku</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#94A3B8]" />
                <Input data-testid="assign-search" value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} placeholder="primatelj, opis, kategorija..." className="pl-8" />
              </div>
            </div>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="assign-plan-list">
            {plans.filter((p) => !assignSearch || `${p.counterparty} ${p.note} ${p.category}`.toLowerCase().includes(assignSearch.toLowerCase())).map((p) => {
              const sel = assignSel.includes(p.id);
              return (
                <div key={p.id} data-testid={`assign-plan-${p.id}`} onClick={() => setAssignSel((s) => (sel ? s.filter((x) => x !== p.id) : [...s, p.id]))} className={`flex items-center gap-3 border rounded-sm px-3 py-2 cursor-pointer transition-colors duration-150 ${sel ? "border-[#0D9488]/50 bg-[#0D9488]/5" : "border-[#E2E8F0] hover:bg-[#F8FAFC]"}`}>
                  <Checkbox checked={sel} className="pointer-events-none" tabIndex={-1} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-[#0F172A] truncate">{p.counterparty || p.category}</span>
                    <span className="block text-xs text-[#64748B] truncate">{fmtDate(p.date)} · {p.type === "naplata" ? "naplata" : "plaćanje"} · {p.category}{p.note ? ` · ${p.note}` : ""}</span>
                  </span>
                  <span className={`font-semibold tabular-nums text-sm shrink-0 ${p.type === "naplata" ? "text-[#0D9488]" : "text-[#DC2626]"}`}>
                    {p.type === "naplata" ? "+" : "−"}{fmtNum(p.amount, p.currency === "EUR" ? "€" : "KM")}
                  </span>
                </div>
              );
            })}
            {plans.length === 0 && <p className="text-sm text-[#64748B]">Nema planiranih stavki bez vozila.</p>}
          </div>
          <div className="flex gap-2 justify-end">
            <Button data-testid="assign-cancel-btn" variant="outline" onClick={() => setAssignOpen(false)} className="rounded-sm border-[#CBD5E1]"><X className="w-4 h-4 mr-1" /> Odustani</Button>
            <Button data-testid="assign-confirm-btn" disabled={!assignVeh || assignSel.length === 0 || busy} onClick={confirmAssign} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
              <Check className="w-4 h-4 mr-1" /> Poveži ({assignSel.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={leaseOpen} onOpenChange={setLeaseOpen}>
        <DialogContent data-testid="lease-dialog" className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Repeat className="w-4 h-4 text-[#0D9488]" /> Ručni unos dugoročnog najma</DialogTitle>
            <DialogDescription>Kreira fiksni mjesečni prihod za vozilo — rate se prebacuju u planirane transakcije na stranici Fiksni troškovi/prihodi.</DialogDescription>
          </DialogHeader>
          {lease && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Vozilo</Label>
                <Select value={lease.vehicle} onValueChange={(v) => setLease((l) => ({ ...l, vehicle: v }))}>
                  <SelectTrigger data-testid="lease-vehicle-select"><SelectValue placeholder="Odaberi vozilo" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => <SelectItem key={v.id} value={v.plate}>{v.plate}{v.label ? ` — ${v.label}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Najmoprimac</Label>
                <Input data-testid="lease-counterparty" value={lease.counterparty} onChange={(e) => setLease((l) => ({ ...l, counterparty: e.target.value }))} placeholder="npr. Firma XYZ d.o.o." />
              </div>
              <div className="space-y-1.5">
                <Label>Mjesečni iznos</Label>
                <Input data-testid="lease-amount" inputMode="decimal" value={lease.amount} onChange={(e) => setLease((l) => ({ ...l, amount: e.target.value }))} placeholder="800" />
              </div>
              <div className="space-y-1.5">
                <Label>Valuta</Label>
                <Select value={lease.currency} onValueChange={(v) => setLease((l) => ({ ...l, currency: v }))}>
                  <SelectTrigger data-testid="lease-currency"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="BAM">BAM (KM)</SelectItem><SelectItem value="EUR">EUR (€)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Banka (očekivana uplata)</Label>
                <Select value={lease.bank_id} onValueChange={(v) => setLease((l) => ({ ...l, bank_id: v }))}>
                  <SelectTrigger data-testid="lease-bank"><SelectValue /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.short || b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Prvo dospijeće</Label>
                <Input data-testid="lease-first-due" type="date" value={lease.first_due} onChange={(e) => setLease((l) => ({ ...l, first_due: e.target.value }))} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Vrijedi do (opcionalno, zadnji mjesec)</Label>
                <Input data-testid="lease-end-month" type="month" value={lease.end_month} onChange={(e) => setLease((l) => ({ ...l, end_month: e.target.value }))} />
              </div>
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button data-testid="lease-cancel-btn" variant="outline" onClick={() => setLeaseOpen(false)} className="rounded-sm border-[#CBD5E1]"><X className="w-4 h-4 mr-1" /> Odustani</Button>
            <Button data-testid="lease-save-btn" disabled={busy} onClick={saveLease} className="bg-[#0D9488] hover:bg-[#0F766E] rounded-sm">
              <Check className="w-4 h-4 mr-1" /> Spremi najam
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
